<template>
  <aside :class="ns.b()" @click="closeHistoryMore">
    <label class="sidebar-ai__search" @click.stop>
      <z-icon class="sidebar-ai__search-icon"><Search /></z-icon>
      <input
        ref="searchInputRef"
        v-model="searchKeyword"
        type="search"
        class="sidebar-ai__search-input"
        placeholder="搜索..."
        autocomplete="off"
        spellcheck="false"
        @keydown.esc.prevent="clearSearch"
      />
      <kbd class="sidebar-ai__search-kbd">Ctrl K</kbd>
    </label>

    <div class="sidebar-ai__brand" @click.stop>
      <span class="sidebar-ai__avatar" aria-hidden="true">{{
        brandAvatar
      }}</span>
      <span class="sidebar-ai__brand-name">{{ brandName }}</span>
    </div>

    <nav class="sidebar-ai__nav" aria-label="快捷入口" @click.stop>
      <button
        v-for="item in primaryNav"
        :key="item.key"
        type="button"
        class="sidebar-ai__nav-item"
        :class="{ 'is-active': isNavActive(item.key) }"
        :title="item.label"
        @click="handlePrimaryNav(item.key)"
      >
        <z-icon class="sidebar-ai__nav-icon"
          ><component :is="item.icon"
        /></z-icon>
        <span>{{ item.label }}</span>
      </button>
    </nav>

    <z-scrollbar class="sidebar-ai__scroll">
      <p class="sidebar-ai__section sidebar-ai__section--history">历史对话</p>
      <p v-if="historyLoading" class="sidebar-ai__hint">加载中…</p>
      <p v-else-if="historyError" class="sidebar-ai__hint is-error">
        {{ historyError }}
      </p>
      <p
        v-else-if="!filteredConversationGroups.length"
        class="sidebar-ai__hint"
      >
        {{
          searchKeyword.trim() || conversations.length
            ? '无匹配对话'
            : '暂无历史，发一条消息后会出现在这里。'
        }}
      </p>
      <div v-else class="sidebar-ai__history">
        <section
          v-for="group in filteredConversationGroups"
          :key="group.key"
          class="sidebar-ai__group"
        >
          <h3 class="sidebar-ai__group-label">{{ group.label }}</h3>
          <div
            v-for="item in group.items"
            :key="item.id"
            class="sidebar-ai__hist-item"
            :class="{
              'is-active': activeSessionId === item.id,
              'menu-open': historyMoreId === item.id || renamingId === item.id,
            }"
            role="button"
            tabindex="0"
            @click.stop="onHistoryItemClick(item)"
            @keydown.enter.prevent="onHistoryItemClick(item)"
          >
            <input
              v-if="renamingId === item.id"
              class="sidebar-ai__rename"
              :data-rename-id="item.id"
              v-model="renameDraft"
              maxlength="80"
              @click.stop
              @keydown.enter.prevent="commitRename(item)"
              @keydown.esc.prevent="cancelRename"
              @blur="commitRename(item)"
            />
            <span v-else class="sidebar-ai__hist-title">{{ item.title }}</span>
            <div class="sidebar-ai__hist-more" @click.stop>
              <button
                type="button"
                class="sidebar-ai__more-btn"
                title="更多"
                aria-label="更多"
                :aria-expanded="historyMoreId === item.id"
                @click="toggleHistoryMore(item.id, $event)"
              >
                ···
              </button>
              <div
                v-if="historyMoreId === item.id"
                class="sidebar-ai__more-menu"
                role="menu"
              >
                <button
                  type="button"
                  role="menuitem"
                  @click="requestShareConversation(item, $event)"
                >
                  分享
                </button>
                <button
                  type="button"
                  role="menuitem"
                  @click="onBeginRename(item, $event)"
                >
                  重命名
                </button>
                <button
                  type="button"
                  role="menuitem"
                  @click="togglePin(item, $event)"
                >
                  {{ item.pinned ? '取消置顶' : '置顶' }}
                </button>
                <button
                  type="button"
                  role="menuitem"
                  class="is-danger"
                  @click="removeConversation(item, $event)"
                >
                  删除
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>
    </z-scrollbar>
  </aside>
</template>

<script lang="ts" setup>
import {
  computed,
  nextTick,
  onMounted,
  onUnmounted,
  ref,
  type Component,
} from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { Cpu, EditPen, Search } from '@zoryn/icons'
import { useNamespace } from '@zoryn/hooks'
import { useAiChatNav } from './composables/use-ai-chat-nav'
import type { SeekerConversation } from './types'

defineOptions({ name: 'ZAiSidePanel' })

const ns = useNamespace('ai-side-panel')

const props = withDefaults(
  defineProps<{
    brandName?: string
    brandAvatar?: string
    chatPath?: string
  }>(),
  {
    brandName: '屿枫',
    brandAvatar: '屿',
    chatPath: '/ai/chat',
  }
)

const emit = defineEmits<{
  'ensure-ai-mode': []
}>()

type NavKey = 'new-chat' | 'skills'

const route = useRoute()
const router = useRouter()
const {
  activeSessionId,
  conversations,
  conversationGroups,
  historyLoading,
  historyError,
  historyMoreId,
  renamingId,
  renameDraft,
  createNewChat,
  openConversation,
  refreshConversations,
  toggleHistoryMore,
  closeHistoryMore,
  togglePin,
  beginRename,
  cancelRename,
  commitRename,
  removeConversation,
  requestShareConversation,
} = useAiChatNav()

const searchInputRef = ref<HTMLInputElement | null>(null)
const searchKeyword = ref('')
const activeNavKey = ref<NavKey | null>('new-chat')

const primaryNav: { key: NavKey; label: string; icon: Component }[] = [
  { key: 'new-chat', label: '新对话', icon: EditPen },
  { key: 'skills', label: '技能', icon: Cpu },
]

const keywordLower = computed(() => searchKeyword.value.trim().toLowerCase())

const filteredConversationGroups = computed(() => {
  const q = keywordLower.value
  if (!q) return conversationGroups.value
  return conversationGroups.value
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => item.title.toLowerCase().includes(q)),
    }))
    .filter((group) => group.items.length > 0)
})

function isNavActive(key: NavKey): boolean {
  if (key === 'new-chat') {
    return (
      activeNavKey.value === 'new-chat' &&
      !activeSessionId.value &&
      route.path.startsWith(props.chatPath)
    )
  }
  return activeNavKey.value === key && route.path.startsWith(props.chatPath)
}

function clearSearch(): void {
  searchKeyword.value = ''
}

async function ensureAiChatRoute(): Promise<void> {
  emit('ensure-ai-mode')
  if (route.path !== props.chatPath) {
    await router.push(props.chatPath)
  }
}

async function handleNewChat(): Promise<void> {
  activeNavKey.value = 'new-chat'
  await ensureAiChatRoute()
  await nextTick()
  createNewChat()
}

async function handlePrimaryNav(key: NavKey): Promise<void> {
  if (key === 'new-chat') {
    await handleNewChat()
    return
  }
  if (key === 'skills') {
    activeNavKey.value = 'skills'
    await ensureAiChatRoute()
    document
      .querySelector('.sidebar-ai__section')
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
}

function onHistoryItemClick(item: SeekerConversation): void {
  if (renamingId.value === item.id) return
  activeNavKey.value = null
  openConversation(item.id)
  ensureAiChatRoute()
}

function onBeginRename(item: SeekerConversation, event: Event): void {
  beginRename(item, event)
  void nextTick(() => {
    const input = document.querySelector(
      `.sidebar-ai__rename[data-rename-id="${CSS.escape(item.id)}"]`
    ) as HTMLInputElement | null
    input?.focus()
    input?.select()
  })
}

function onGlobalKeydown(event: KeyboardEvent): void {
  if (!(event.ctrlKey || event.metaKey) || event.key.toLowerCase() !== 'k')
    return
  if (event.defaultPrevented) return
  const target = event.target as HTMLElement | null
  if (target?.closest('textarea, [contenteditable="true"]')) return
  event.preventDefault()
  searchInputRef.value?.focus()
  searchInputRef.value?.select()
}

onMounted(() => {
  void refreshConversations()
  window.addEventListener('keydown', onGlobalKeydown)
})

onUnmounted(() => {
  window.removeEventListener('keydown', onGlobalKeydown)
})
</script>
